import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { EASE } from "@/components/atoms";

export const openCvRequest = () => window.dispatchEvent(new CustomEvent("open-cv-request"));

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const CvRequestModal = () => {
  const { t, lang } = useLanguage();
  const cv = t.cv;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | done | invalid | error
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [variants, setVariants] = useState({ main: true, ped: false, office: false });

  const toggleVariant = (key) =>
    setVariants((v) => {
      const next = { ...v, [key]: !v[key] };
      // Mindestens eine Variante muss gewaehlt bleiben
      if (!next.main && !next.ped && !next.office) return v;
      return next;
    });

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-cv-request", handler);
    return () => window.removeEventListener("open-cv-request", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !EMAIL_RE.test(form.email.trim())) {
      setStatus("invalid");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          lang,
          variants: ["main", "ped", "office"].filter((k) => variants[k]),
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setStatus("done");
      setForm({ name: "", email: "", company: "" });
      setVariants({ main: true, ped: false, office: false });
    } catch {
      setStatus("error");
    }
  };

  const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-smoke";
  const fieldCls =
    "w-full border-b border-ink/30 bg-transparent py-3 text-base font-light placeholder:text-smoke/60 focus:border-proof focus:outline-none transition-colors duration-300";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[210] bg-ink/90 flex items-center justify-center p-5"
          onClick={() => setOpen(false)}
          data-testid="cv-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="bg-paper border border-ink w-full max-w-md p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            {status === "done" ? (
              <div data-testid="cv-success-message">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-proof">— {cv.requestLabel}</p>
                <h3 className="mt-5 font-serif font-light tracking-tight text-3xl md:text-4xl">{cv.successTitle}</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-ink/80">{cv.successText}</p>
                <button
                  data-testid="cv-success-close"
                  onClick={() => setOpen(false)}
                  className="mt-8 w-full border border-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper"
                >
                  {cv.close}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-serif font-light tracking-tight text-3xl md:text-4xl" data-testid="cv-modal-title">
                    {cv.title}
                  </h3>
                  <button
                    data-testid="cv-modal-close"
                    onClick={() => setOpen(false)}
                    aria-label={cv.close}
                    className="font-mono text-xl leading-none text-smoke transition-colors duration-300 hover:text-ink"
                  >
                    ×
                  </button>
                </div>
                <p className="mt-4 text-sm font-light leading-relaxed text-smoke">{cv.intro}</p>
                <form onSubmit={submit} className="mt-8 space-y-6" noValidate data-testid="cv-form">
                  <div>
                    <label htmlFor="cv-name" className={labelCls}>
                      {cv.nameLabel} <span className="text-proof">*</span>
                    </label>
                    <input id="cv-name" data-testid="cv-form-name-input" type="text" value={form.name} onChange={set("name")} className={fieldCls} autoComplete="name" />
                  </div>
                  <div>
                    <label htmlFor="cv-email" className={labelCls}>
                      {cv.emailLabel} <span className="text-proof">*</span>
                    </label>
                    <input id="cv-email" data-testid="cv-form-email-input" type="email" value={form.email} onChange={set("email")} className={fieldCls} autoComplete="email" />
                  </div>
                  <div>
                    <label htmlFor="cv-company" className={labelCls}>
                      {cv.companyLabel} <span className="text-smoke/60 normal-case tracking-normal">({cv.optionalNote})</span>
                    </label>
                    <input id="cv-company" data-testid="cv-form-company-input" type="text" value={form.company} onChange={set("company")} className={fieldCls} autoComplete="organization" />
                  </div>
                  <fieldset>
                    <legend className={labelCls}>{cv.variantsLabel}</legend>
                    <div className="mt-3 space-y-3">
                      {[
                        ["main", cv.variantMain],
                        ["ped", cv.variantPed],
                        ["office", cv.variantOffice],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          role="checkbox"
                          aria-checked={variants[key]}
                          data-testid={`cv-variant-${key}`}
                          onClick={() => toggleVariant(key)}
                          className="flex items-start gap-3 text-left group"
                        >
                          <span
                            className={`mt-0.5 w-4 h-4 shrink-0 border flex items-center justify-center transition-colors duration-300 ${
                              variants[key] ? "border-proof bg-proof" : "border-ink/40 group-hover:border-ink"
                            }`}
                          >
                            {variants[key] && <span className="w-1.5 h-1.5 bg-paper" />}
                          </span>
                          <span className="text-sm font-light leading-snug text-ink/85">{label}</span>
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  {(status === "invalid" || status === "error") && (
                    <p data-testid="cv-form-error" className="font-mono text-[11px] leading-relaxed tracking-[0.05em] text-proof">
                      {status === "invalid" ? cv.errorFields : cv.errorGeneric}
                    </p>
                  )}
                  <button
                    data-testid="cv-form-submit-button"
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full border border-ink bg-ink text-paper px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-transparent hover:text-ink disabled:opacity-50"
                  >
                    {status === "sending" ? cv.sending : cv.submit}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
